import { AppConfig } from '@/config/app';
import logger from '@/lib/edge-logger';
import { parseColumns } from '@/utils/dynamic-function-render';
import { getMasterDataRecord } from './masterdata-action';

// 缓存配置数据的单例模式实现
// 缓存配置数据
const bookingConfigCache: Record<string, any> = {};
const bookingConfigPromises: Record<string, Promise<any> | null> = {};

/**
 * 清除托单配置缓存
 * @param type 配置类型
 * @param code 配置代码
 */
export const clearBookingConfigCache = (type?: string, code?: string) => {
  if (type && code) {
    // 清除特定配置的缓存
    const cacheKey = `${type}/${code}`;
    delete bookingConfigCache[cacheKey];
    delete bookingConfigPromises[cacheKey];
  } else {
    // 清除所有配置缓存
    Object.keys(bookingConfigCache).forEach(key => delete bookingConfigCache[key]);
    Object.keys(bookingConfigPromises).forEach(key => delete bookingConfigPromises[key]);
  }
};

/**
 * 从本地文件系统加载配置
 * @param type 配置类型
 * @param code 配置代码
 * @param cacheKey 缓存键名
 * @returns 配置对象
 */
async function loadConfigFromLocal(type: string, code: string, cacheKey: string): Promise<any> {
  const configDir = type.toLowerCase();
  const configModule = await import(`@/config/${configDir}/${code}`);
  const config = configModule.default || null;

  // 存储到缓存
  bookingConfigCache[cacheKey] = config;
  logger.info('getBookingConfig from local', cacheKey, config);

  return config;
}

/**
 * 从API获取配置
 * @param doc 文档类型（小写，如 'booking' / 'so'）
 * @param code 配置代码（如 'form_schema' / 'list_columns'）
 * @returns 配置对象Promise
 *
 * 主数据类别按 doc 推导：booking→BookingRenderConfig，so→SoRenderConfig；
 * id_content 约定：统一沿用 ${doc}_${code}（SO 表单即 so_form_schema，create/edit/view 通用）。
 */
async function loadConfigJson(doc: string, code: string) {
  const category = `${doc.charAt(0).toUpperCase()}${doc.slice(1)}RenderConfig`;
  const idContent = `${doc}_${code}`;
  return (await getMasterDataRecord(category, AppConfig.masterdata.defaultVersion, idContent)).content.content;
}

/**
 * 加载 ListColumns\ExportColumns 配置
 * @param cacheKey 缓存键名
 * @returns 解析后的配置对象
 */
async function loadColumnsConfig(doc: string, code: string, cacheKey: string) {
  const jsonConfig = await loadConfigJson(doc, code);
  logger.info('loadConfigJson', cacheKey, jsonConfig);

  // 使用parseColumns解析配置
  const parsedConfig = await parseColumns(jsonConfig);

  // 存储到缓存
  bookingConfigCache[cacheKey] = parsedConfig;

  return bookingConfigCache[cacheKey];
}

/**
 * 加载 FormSchema 配置
 * @param cacheKey 缓存键名
 * @returns 解析后的配置对象
 *
 * SO：DB 为唯一来源（SoRenderConfig / so_form_schema），本地 FormSchema.ts 已废弃；
 *     DB 查不到直接抛错，不回落本地。
 * booking 等：DB 优先，查不到回落本地 @/config/${doc}/FormSchema（booking_form_schema 未种 DB）。
 */
async function loadFormSchemaConfig(doc: string, code: string, cacheKey: string) {
  let jsonConfig: any;
  try {
    jsonConfig = await loadConfigJson(doc, code);
  } catch (error) {
    if (doc === 'so') {
      throw new Error(`未加载到 SO 表单 schema（DB SoRenderConfig / so_form_schema 记录不存在）：${(error as any)?.message || error}`);
    }
    logger.warn(`loadFormSchemaConfig: DB lookup failed for ${cacheKey}, falling back to local`, error);
  }
  if (jsonConfig) {
    logger.info('loadConfigJson', cacheKey, jsonConfig);
    bookingConfigCache[cacheKey] = jsonConfig;
    return bookingConfigCache[cacheKey];
  }
  if (doc === 'so') {
    throw new Error('未加载到 SO 表单 schema（DB SoRenderConfig / so_form_schema 记录内容为空）');
  }
  return await loadConfigFromLocal(doc, 'FormSchema', cacheKey);
}


/**
 * 获取托单配置
 * @param doc 文档类型
 * @param code 配置代码
 * @returns 配置对象Promise
 */
export const getBookingConfig = async (doc: string, code: string) => {
  const docLowerCase = doc.toLowerCase();


  const cacheKey = `${docLowerCase}/${code}`;

  // 如果缓存中已有结果，直接返回
  if (bookingConfigCache[cacheKey]) {
    logger.info('getBookingConfig from cache', cacheKey, bookingConfigCache[cacheKey]);
    return bookingConfigCache[cacheKey];
  }

  // 特殊处理 ListColumns 配置
  if (code === 'ListColumns') {
    return await loadColumnsConfig(docLowerCase, 'list_columns', cacheKey);
  }
  // 特殊处理 ListColumns 配置
  if (code === 'ExportColumns') {
    return await loadColumnsConfig(docLowerCase, 'export_columns', cacheKey);
  }
  // 特殊处理 FormSchema 配置
  if (code === 'FormSchema') {
    return await loadFormSchemaConfig(docLowerCase, 'form_schema', cacheKey);
  }

  return await loadConfigFromLocal(doc, code, cacheKey);
}
